/*
  Mood tracking helpers
  - Stores per-day aggregates in rl_daily_mood_v1
  - Aggregates are unlocked for today, locked for past days
  - Computes from diary posts in rl_diary_posts_v1
*/

import { getNow } from "./timewarp";

const POSTS_KEY = "rl_diary_posts_v1";
const DAILY_MOOD_KEY = "rl_daily_mood_v1"; // { [dateKey]: { sum, count, avg, locked } }

export function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function todayKey() {
  return toDateKey(new Date(getNow()));
}

export function loadAllPosts() {
  try {
    const raw = localStorage.getItem(POSTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function loadDailyMoodMap() {
  try {
    const raw = localStorage.getItem(DAILY_MOOD_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveDailyMoodMap(map) {
  try { localStorage.setItem(DAILY_MOOD_KEY, JSON.stringify(map)); } catch {}
}

export function computeDayAggregateFromPosts(dateKey, opts = {}) {
  const { sinceTs = null } = opts;
  const all = loadAllPosts();
  const arr = all[dateKey] || [];
  let sum = 0;
  let count = 0;
  for (const p of arr) {
    if (sinceTs && (p.ts || 0) < sinceTs) continue; // exclude earlier-than-sinceTs
    if (typeof p.mood !== "number") continue; // exclude mood-less entries
    const v = Math.max(1, Math.min(10, Math.round(p.mood)));
    sum += v;
    count += 1;
  }
  const avg = count ? +(sum / count).toFixed(2) : null;
  return { sum, count, avg };
}

export function recomputeAndSaveDay(dateKey) {
  const map = loadDailyMoodMap();
  const isToday = dateKey === todayKey();
  const day = map[dateKey];
  // if locked and not today, do not change (past day)
  if (day && day.locked && !isToday) return map;
  const { sum, count, avg } = computeDayAggregateFromPosts(dateKey);
  if (!count) {
    // no moods for the day
    if (isToday) {
      map[dateKey] = { sum: 0, count: 0, avg: null, locked: false };
    } else {
      // past days without moods: remove entry if exists
      if (map[dateKey]) delete map[dateKey];
    }
  } else {
    map[dateKey] = { sum, count, avg, locked: !!(day && day.locked && !isToday) ? true : false };
    // if we re-saved today, ensure unlocked
    if (isToday) map[dateKey].locked = false;
  }
  saveDailyMoodMap(map);
  return map;
}

export function finalizePastDays() {
  const map = loadDailyMoodMap();
  const posts = loadAllPosts();
  const tKey = todayKey();
  // For each past day that has posts but no locked record yet, compute and lock once
  for (const dateKey of Object.keys(posts)) {
    if (dateKey < tKey) {
      const existing = map[dateKey];
      if (existing && existing.locked) continue; // already finalized; do not recompute
      const agg = computeDayAggregateFromPosts(dateKey);
      if (agg.count) {
        map[dateKey] = { sum: agg.sum, count: agg.count, avg: agg.avg, locked: true };
      } else {
        // no moods for that day: ensure no record
        if (map[dateKey]) delete map[dateKey];
      }
    }
  }
  // Ensure any existing past entries are locked (never recompute their sums)
  for (const dateKey of Object.keys(map)) {
    if (dateKey < tKey) map[dateKey].locked = true;
  }
  // Today remains dynamic; recompute each call
  const todayAgg = computeDayAggregateFromPosts(tKey);
  map[tKey] = { sum: todayAgg.sum, count: todayAgg.count, avg: todayAgg.avg, locked: false };
  saveDailyMoodMap(map);
  return map;
}

// Gradient utilities
export function moodValueToColor(v) {
  // v in 1..10; map 1 red(#FF0000) - 5 yellow(#FFD93D) - 10 green(#00C853)
  const red = [255, 0, 0];
  const yellow = [255, 217, 61];
  const green = [0, 200, 83];
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
  const val = clamp(v || 0, 1, 10);
  let col;
  if (val <= 5) {
    const t = (val - 1) / 4; // 0..1
    col = [
      Math.round(red[0] + t * (yellow[0] - red[0])),
      Math.round(red[1] + t * (yellow[1] - red[1])),
      Math.round(red[2] + t * (yellow[2] - red[2]))
    ];
  } else {
    const t = (val - 5) / 5; // 0..1
    col = [
      Math.round(yellow[0] + t * (green[0] - yellow[0])),
      Math.round(yellow[1] + t * (green[1] - yellow[1])),
      Math.round(yellow[2] + t * (green[2] - yellow[2]))
    ];
  }
  return `rgb(${col[0]}, ${col[1]}, ${col[2]})`;
}

export function moodGradientCss() {
  return "linear-gradient(90deg, #FF0000 0%, #FFD93D 50%, #00C853 100%)";
}

// Build dataset for graph based on time window
export function buildDatasetForWindow({ days, includeToday = true }) {
  // Ensure we recompute/lock before building a new dataset (handles resets + timewarp jumps)
  finalizePastDays();
  const map = finalizePastDays(); // ensures up-to-date locking
  const out = [];
  const now = new Date(getNow());
  const today = toDateKey(now);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = toDateKey(d);
    const isToday = key === today;
    const row = map[key];
    if (!row) {
      if (isToday && includeToday) {
        out.push({ dateKey: key, avg: row ? row.avg : null });
      }
      continue;
    }
    if (row.locked || (isToday && includeToday)) {
      out.push({ dateKey: key, avg: row.avg });
    }
  }
  return out;
}

// Build dataset for newest streak start timestamp
export function buildDatasetSinceTimestamp(streakStartTs) {
  // Keep aggregates up to date before deriving a filtered dataset
  finalizePastDays();
  // Scan posts, group by dateKey, but include only posts with ts >= streakStartTs
  if (!streakStartTs) return [];
  const posts = loadAllPosts();
  const keys = Object.keys(posts).sort();
  const outMap = {};
  for (const key of keys) {
    const arr = posts[key] || [];
    let sum = 0, count = 0;
    for (const p of arr) {
      if ((p.ts || 0) < streakStartTs) continue;
      if (typeof p.mood !== "number") continue;
      const v = Math.max(1, Math.min(10, Math.round(p.mood)));
      sum += v; count += 1;
    }
    if (count) {
      outMap[key] = { avg: +(sum / count).toFixed(2) };
    }
  }
  // Return sorted by dateKey
  return Object.keys(outMap).sort().map(k => ({ dateKey: k, avg: outMap[k].avg }));
}